import React, { useEffect } from "react";
import { Form, Modal, Row, Col } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import FalconCloseButton from "components/common/FalconCloseButton";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import MultiSelect from "components/common/MultiSelect";
import {
  GetAllEncounterClass,
  GetAllEncounterInfo,
  GetAllSpecilaity,
} from "features/preauthorization/config/preauthorizationApi";
import { handleFillStepData } from "../config/claimSlice";

const EditEncounterDetailsRequestModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const { stepFormData, viewClaimInfo, preauthorizationDiagnosis } =
    useSelector((state) => state.claim);
  const {
    preauthorizationRequest,
    encounterClass,
    discharegSpeciality,
    serviceTypeListEncounter,
    actPeriortyList,
    triageCategoryList,
    emergencyArrivalList,
    departmentDispositionList,
    lengthOfStayList,
    encounterStatusList,
    adminResourceList,
    dischargeDispositionList,
    reAdmissionList,
    eventTypeList,
    admissionSpeciality,
  } = useSelector((state) => state.preauthorization);

  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    if (discharegSpeciality?.length === 0) {
      dispatch(GetAllSpecilaity());
    }
    if (encounterStatusList?.length === 0) {
      dispatch(GetAllEncounterInfo());
    }
    if (encounterClass?.length === 0) {
      dispatch(GetAllEncounterClass());
    }
  }, []);

  // Initialize form with stepFormData
  useEffect(() => {
    if (stepFormData) {
      Object.entries(stepFormData).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [stepFormData, setValue]);

  useEffect(() => {
    if (show) {
      setValue("admission_date", stepFormData?.admission_date || null);
      setValue("discharge_date", stepFormData?.discharge_date || null);
      setValue("triage_date", stepFormData?.triage_date || null);
      setValue(
        "triage_category",
        triageCategoryList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.triage_category?.enctalltyp_code
        ) || null
      );
      setValue(
        "encounter_class",
        encounterClass?.find(
          (item) => item?.act_code === stepFormData?.encounter_class?.act_code
        ) || null
      );
      setValue(
        "encounter_status",
        encounterStatusList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.encounter_status?.enctalltyp_code
        ) || null
      );
      setValue(
        "discharge_speciality",
        discharegSpeciality?.find(
          (item) =>
            item?.specialities_code ===
            stepFormData?.discharge_speciality?.specialities_code
        ) || null
      );
      setValue(
        "admission_speciality",
        discharegSpeciality?.find(
          (item) =>
            item?.specialities_code ===
            stepFormData?.admission_speciality?.specialities_code
        ) || null
      );
      setValue(
        "care_type",
        serviceTypeListEncounter?.find(
          (item) =>
            item?.enctalltyp_code === stepFormData?.care_type?.enctalltyp_code
        ) || null
      );
      setValue(
        "encounter_admitsource",
        adminResourceList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.encounter_admitsource?.enctalltyp_code
        ) || null
      );
      setValue(
        "service_event_type",
        eventTypeList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.service_event_type?.enctalltyp_code
        ) || null
      );
      setValue(
        "discharge_disposition",
        dischargeDispositionList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.discharge_disposition?.enctalltyp_code
        ) || null
      );
      setValue(
        "emer_department_disposition",
        departmentDispositionList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.emer_department_disposition?.enctalltyp_code
        ) || null
      );
      setValue(
        "intend_length_stay",
        lengthOfStayList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.intend_length_stay?.enctalltyp_code
        ) || null
      );
      setValue(
        "cause_of_death",
        preauthorizationDiagnosis?.find(
          (item) => item?.code === stepFormData?.cause_of_death?.code
        ) || null
      );
      setValue(
        "emergncy_arrival_code",
        emergencyArrivalList?.find(
          (item) =>
            item?.enctalltyp_code ===
            stepFormData?.emergncy_arrival_code?.enctalltyp_code
        ) || null
      );
      setValue(
        "last_menstrual_code",
        stepFormData?.last_menstrual_code || null
      );
    }
  }, [show, stepFormData, setValue]);

  // Set default act_priority
  useEffect(() => {
    setValue(
      "act_priority",
      actPeriortyList?.find((item) => item?.code === "EL") || {
        code: "EL",
        name: "Elective",
      }
    );
  }, [show, dispatch, actPeriortyList]);

  // Conditional logic
  const isInstitual =
    stepFormData?.claim_type?.claim_types_code?.toLowerCase() ===
      "institutional" ||
    viewClaimInfo?.claim_type?.toLowerCase() === "institutional";

  const isInstitutional =
    (stepFormData?.claim_type?.claim_types_code?.toLowerCase() ===
      "institutional" ||
      viewClaimInfo?.claim_type?.toLowerCase() === "institutional") ??
    false;

  const isProfessionalOut =
    (stepFormData?.claim_type?.claim_types_code === "professional" ||
      viewClaimInfo?.claim_type?.toLowerCase() === "professional") &&
    stepFormData?.claim_sub_type?.claimsub_types_code === "op";

  const isProfessionalEmer =
    (stepFormData?.claim_type?.claim_types_code === "professional" ||
      viewClaimInfo?.claim_type?.toLowerCase() === "professional") &&
    stepFormData?.claim_sub_type?.claimsub_types_code === "emr";

  const isDental =
    (stepFormData?.claim_type?.claim_types_code?.toLowerCase() === "oral" ||
      viewClaimInfo?.claim_type?.toLowerCase() === "oral") ??
    false;

  const isNCHI =
    (viewClaimInfo?.insurance_company_id === 41 ||
      stepFormData?.payer?.insurance_company_id === 41) ??
    false;

  const isEmptyVisitReason = !stepFormData?.visit_reason;

  const dischargeReq =
    isInstitutional &&
    !isNCHI &&
    (stepFormData?.discharge_date ||
      stepFormData?.discharge_speciality ||
      stepFormData?.discharge_disposition);

  const handleFormSubmit = (data) => {
    dispatch(
      handleFillStepData({
        check: "admission_date",
        value: data?.admission_date,
      })
    );
    dispatch(
      handleFillStepData({
        check: "discharge_date",
        value: data?.discharge_date,
      })
    );
    dispatch(
      handleFillStepData({
        check: "triage_date",
        value: data?.triage_date,
      })
    );
    dispatch(
      handleFillStepData({
        check: "triage_category",
        value: data?.triage_category,
      })
    );
    dispatch(
      handleFillStepData({
        check: "emergncy_arrival_code",
        value: data?.emergncy_arrival_code,
      })
    );
    dispatch(
      handleFillStepData({
        check: "emer_department_disposition",
        value: data?.emer_department_disposition,
      })
    );
    dispatch(
      handleFillStepData({
        check: "emergency_service_start_date",
        value: data?.emergency_service_start_date,
      })
    );
    dispatch(
      handleFillStepData({
        check: "cause_of_death",
        value: data?.cause_of_death,
      })
    );
    dispatch(
      handleFillStepData({
        check: "encounter_class",
        value: data?.encounter_class,
      })
    );
    dispatch(
      handleFillStepData({
        check: "encounter_status",
        value: data?.encounter_status,
      })
    );
    dispatch(
      handleFillStepData({
        check: "service_event_type",
        value: data?.service_event_type,
      })
    );
    dispatch(
      handleFillStepData({
        check: "care_type",
        value: data?.care_type,
      })
    );
    dispatch(
      handleFillStepData({
        check: "act_priority",
        value: data?.act_priority,
      })
    );
    dispatch(
      handleFillStepData({
        check: "admission_speciality",
        value: data?.admission_speciality,
      })
    );
    dispatch(
      handleFillStepData({
        check: "discharge_disposition",
        value: data?.discharge_disposition,
      })
    );
    dispatch(
      handleFillStepData({
        check: "intend_length_stay",
        value: data?.intend_length_stay,
      })
    );
    dispatch(
      handleFillStepData({
        check: "discharge_speciality",
        value: data?.discharge_speciality,
      })
    );
    dispatch(
      handleFillStepData({
        check: "encounter_admitsource",
        value: data?.encounter_admitsource,
      })
    );
    dispatch(
      handleFillStepData({
        check: "empoly_impact_start_date",
        value: data?.empoly_impact_start_date,
      })
    );
    dispatch(
      handleFillStepData({
        check: "empoly_impact_end_date",
        value: data?.empoly_impact_end_date,
      })
    );
    dispatch(
      handleFillStepData({
        check: "estimated_length_stay",
        value: data?.estimated_length_stay,
      })
    );
    dispatch(
      handleFillStepData({
        check: "last_menstrual_code",
        value: data?.last_menstrual_code,
      })
    );
    reset(); // Reset form after submission
    onHide(); // Close modal
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  return (
    <Modal show={show} size="xl" centered scrollable>
      <Modal.Header>
        <Modal.Title className="fs-10">Encounter Details</Modal.Title>
        <FalconCloseButton
          onClick={handleClose}
          size="sm"
          className="position-absolute top-0 end-0 me-2 mt-2"
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <Row>
            {/* Admission Date */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <BootDatePicker
                name="admission_date"
                label="Admission Date"
                size="sm"
                control={control}
                rules={{
                  required: dischargeReq,
                }}
                selected={watch("admission_date")}
                onChange={(newValue) => {
                  setValue("admission_date", newValue);
                  const dischargeValue = watch("discharge_date");
                  if (dischargeValue && newValue) {
                    const admissionDate =
                      newValue instanceof Date ? newValue : new Date(newValue);
                    const dischargeDate =
                      dischargeValue instanceof Date
                        ? dischargeValue
                        : new Date(dischargeValue);
                    const normalizedAdmission = new Date(
                      admissionDate.getFullYear(),
                      admissionDate.getMonth(),
                      admissionDate.getDate()
                    );
                    const normalizedDischarge = new Date(
                      dischargeDate.getFullYear(),
                      dischargeDate.getMonth(),
                      dischargeDate.getDate()
                    );
                    if (normalizedDischarge < normalizedAdmission) {
                      setError("discharge_date", {
                        type: "custom",
                        message:
                          "Discharge date must be after or equal to admission date",
                      });
                    } else {
                      clearErrors("discharge_date");
                    }
                  } else {
                    clearErrors("discharge_date");
                  }
                }}
                dateFormat="dd-mm-yyyy hh:mm"
                showTimeSelect
                isRequired={true}
              />
              {errors.admission_date && (
                <Form.Text className="text-danger">
                  {errors.admission_date.message}
                </Form.Text>
              )}
            </Col>

            {/* Discharge Date */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <BootDatePicker
                name="discharge_date"
                label="Discharge Date"
                size="sm"
                control={control}
                rules={{
                  required: dischargeReq,
                  validate: (value) => {
                    const admissionValue = watch("admission_date");
                    if (!admissionValue || !value) return true;
                    const admissionDate =
                      admissionValue instanceof Date
                        ? admissionValue
                        : new Date(admissionValue);
                    const dischargeDate =
                      value instanceof Date ? value : new Date(value);
                    if (
                      isNaN(admissionDate.getTime()) ||
                      isNaN(dischargeDate.getTime())
                    )
                      return true;
                    const normalizedAdmission = new Date(
                      admissionDate.getFullYear(),
                      admissionDate.getMonth(),
                      admissionDate.getDate()
                    );
                    const normalizedDischarge = new Date(
                      dischargeDate.getFullYear(),
                      dischargeDate.getMonth(),
                      dischargeDate.getDate()
                    );
                    return (
                      normalizedDischarge >= normalizedAdmission ||
                      "Discharge date must be after or equal to admission date"
                    );
                  },
                }}
                selected={watch("discharge_date")}
                onChange={(newValue) => {
                  setValue("discharge_date", newValue);
                  const admissionValue = watch("admission_date");
                  if (admissionValue && newValue) {
                    const admissionDate =
                      admissionValue instanceof Date
                        ? admissionValue
                        : new Date(admissionValue);
                    const dischargeDate =
                      newValue instanceof Date ? newValue : new Date(newValue);
                    const normalizedAdmission = new Date(
                      admissionDate.getFullYear(),
                      admissionDate.getMonth(),
                      admissionDate.getDate()
                    );
                    const normalizedDischarge = new Date(
                      dischargeDate.getFullYear(),
                      dischargeDate.getMonth(),
                      dischargeDate.getDate()
                    );
                    if (normalizedDischarge < normalizedAdmission) {
                      setError("discharge_date", {
                        type: "custom",
                        message:
                          "Discharge date must be after or equal to admission date",
                      });
                    } else {
                      clearErrors("discharge_date");
                    }
                  } else {
                    clearErrors("discharge_date");
                  }
                }}
                dateFormat="dd-mm-yyyy hh:mm"
                showTimeSelect
                isRequired={dischargeReq}
              />
              {errors.discharge_date && (
                <Form.Text className="text-danger">
                  {errors.discharge_date.message}
                </Form.Text>
              )}
            </Col>

            {/* Triage Date */}
            {(!isInstitual || isNCHI) && !isDental && !isProfessionalOut && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <BootDatePicker
                  name="triage_date"
                  label="Triage Date"
                  size="sm"
                  control={control}
                  rules={{
                    required:
                      (!isNCHI && isProfessionalEmer) ||
                      (isNCHI && isEmptyVisitReason),
                  }}
                  selected={watch("triage_date")}
                  onChange={(newValue) => setValue("triage_date", newValue)}
                  dateFormat="dd-mm-yyyy hh:mm"
                  showTimeSelect
                  maxDate={new Date()}
                  isRequired={
                    (!isNCHI && isProfessionalEmer) ||
                    (isNCHI && isEmptyVisitReason)
                  }
                />
                {errors.triage_date && (
                  <Form.Text className="text-danger">
                    Triage date is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Triage Category */}
            {!isDental && !isProfessionalOut && (!isInstitual || isNCHI) && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Triage Category{" "}
                  {(!isNCHI && isProfessionalEmer) ||
                    (isNCHI && isEmptyVisitReason && (
                      <span className="text-danger">*</span>
                    ))}
                </Form.Label>
                <Controller
                  name="triage_category"
                  control={control}
                  rules={{
                    required:
                      (!isNCHI && isProfessionalEmer) ||
                      (isNCHI && isEmptyVisitReason),
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      isMulti={false}
                      options={triageCategoryList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setValue(
                          "triage_category",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        )
                      }
                      placeholder="Choose ..."
                      isClearable={true}
                    />
                  )}
                />
                {errors.triage_category && (
                  <Form.Text className="text-danger">
                    Triage category is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Encounter Class */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <Form.Label className="">
                Encounter Class
                {((isNCHI && isEmptyVisitReason) || !isNCHI) && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              <Controller
                name="encounter_class"
                control={control}
                rules={{ required: (isNCHI && isEmptyVisitReason) || !isNCHI }}
                render={({ field }) => {
                  // Filter options based on preauthorization type
                  let filteredOptions = encounterClass;

                  if (isDental) {
                    filteredOptions = encounterClass?.filter(
                      (item) => item.act_code === "AMB"
                    );
                  } else if (isProfessionalEmer) {
                    filteredOptions = encounterClass?.filter(
                      (item) => item?.act_code === "EMER"
                    );
                  } else if (isProfessionalOut) {
                    filteredOptions = encounterClass.filter((item) =>
                      ["AMB", "HH", "VR"].includes(item.act_code)
                    );
                  } else if (isInstitual && !isNCHI) {
                    filteredOptions = encounterClass?.filter((item) =>
                      ["SS", "IMP"].includes(item.act_code)
                    );
                  } else if (isNCHI) {
                    filteredOptions = encounterClass?.filter((item) =>
                      ["ACU"].includes(item?.act_code)
                    );
                  }

                  return (
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      options={filteredOptions?.map((item) => ({
                        value: item.act_code,
                        label: item.act_description,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.act_code,
                              label: field.value.act_description,
                            }
                          : null
                      }
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "encounter_class",
                          selected
                            ? {
                                act_code: selected.value,
                                act_description: selected.label,
                              }
                            : null
                        );
                      }}
                      formatOptionLabel={({ label }) => <div>{label}</div>}
                      getOptionValue={(option) => option.value}
                      placeholder="Choose ..."
                    />
                  );
                }}
              />
              {errors.encounter_class && (
                <Form.Text className="text-danger">
                  Encounter class is required
                </Form.Text>
              )}
            </Col>

            {/* Encounter Status */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <Form.Label className="">
                Encounter Status{" "}
                {!isNCHI && <span className="text-danger">*</span>}
              </Form.Label>
              <Controller
                name="encounter_status"
                control={control}
                rules={{ required: !isNCHI }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    menuPlacement="auto"
                    closeMenuOnSelect={false}
                    isMulti={false}
                    options={encounterStatusList?.map((item) => ({
                      value: item.enctalltyp_code,
                      label: item.enctalltyp_name,
                    }))}
                    value={
                      field.value
                        ? {
                            value: field.value.enctalltyp_code,
                            label: field.value.enctalltyp_name,
                          }
                        : null
                    }
                    isClearable={true}
                    onChange={(selected) => {
                      setValue(
                        "encounter_status",
                        selected
                          ? {
                              enctalltyp_code: selected.value,
                              enctalltyp_name: selected.label,
                            }
                          : null
                      );
                    }}
                    formatOptionLabel={({ label }) => <div>{label}</div>}
                    getOptionValue={(option) => option.value}
                    placeholder="Choose ..."
                  />
                )}
              />
              {errors.encounter_status && (
                <Form.Text className="text-danger">
                  Encounter status is required
                </Form.Text>
              )}
            </Col>

            {/* Care Type */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <Form.Label className="">Care Type</Form.Label>
              <Controller
                name="care_type"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    menuPlacement="auto"
                    closeMenuOnSelect={false}
                    isMulti={false}
                    options={serviceTypeListEncounter?.map((item) => ({
                      value: item.enctalltyp_code,
                      label: item.enctalltyp_name,
                    }))}
                    value={
                      field.value
                        ? {
                            value: field.value.enctalltyp_code,
                            label: field.value.enctalltyp_name,
                          }
                        : null
                    }
                    isClearable={true}
                    onChange={(selected) => {
                      setValue(
                        "care_type",
                        selected
                          ? {
                              enctalltyp_code: selected.value,
                              enctalltyp_name: selected.label,
                            }
                          : null
                      );
                    }}
                    formatOptionLabel={({ label }) => <div>{label}</div>}
                    getOptionValue={(option) => option.value}
                    placeholder="Choose ..."
                  />
                )}
              />
            </Col>

            {/* Act Priority */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <Form.Label className="">Act Priority</Form.Label>
              <Controller
                name="act_priority"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    isMulti={false}
                    isDisabled={true}
                    options={actPeriortyList?.map((item) => ({
                      value: item.code,
                      label: item.name,
                    }))}
                    value={
                      field.value
                        ? {
                            value: field.value.code,
                            label: field.value.name,
                          }
                        : {
                            value: "EL",
                            label: "Elective",
                          }
                    }
                    placeholder="Choose ..."
                    isClearable={true}
                  />
                )}
              />
            </Col>

            {/* Discharge Speciality */}
            {isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Discharge Speciality
                  {(watch("discharge_date") ||
                    watch("discharge_disposition")) && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>
                <Controller
                  name="discharge_speciality"
                  control={control}
                  rules={{
                    required:
                      !!watch("discharge_date") ||
                      !!watch("discharge_disposition"),
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      options={discharegSpeciality?.map((item) => ({
                        value: item.specialities_code,
                        label: item.specialities_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.specialities_code,
                              label: field.value.specialities_name,
                            }
                          : null
                      }
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "discharge_speciality",
                          selected
                            ? {
                                specialities_code: selected.value,
                                specialities_name: selected.label,
                              }
                            : null
                        );
                      }}
                      formatOptionLabel={({ label }) => <div>{label}</div>}
                      getOptionValue={(option) => option.value}
                      placeholder="Choose ..."
                    />
                  )}
                />
                {errors.discharge_speciality && (
                  <Form.Text className="text-danger">
                    Discharge speciality is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Admission Speciality */}
            {isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Admission Speciality{" "}
                  {isInstitual && !isNCHI && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>
                <Controller
                  name="admission_speciality"
                  control={control}
                  rules={{
                    required: isInstitual && !isNCHI,
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      options={admissionSpeciality?.map((item) => ({
                        value: item.specialities_code,
                        label: item.specialities_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.specialities_code,
                              label: field.value.specialities_name,
                            }
                          : null
                      }
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "admission_speciality",
                          selected
                            ? {
                                specialities_code: selected.value,
                                specialities_name: selected.label,
                              }
                            : null
                        );
                      }}
                      formatOptionLabel={({ label }) => <div>{label}</div>}
                      getOptionValue={(option) => option.value}
                      placeholder="Choose ..."
                    />
                  )}
                />
                {errors.admission_speciality && (
                  <Form.Text className="text-danger">
                    Admission speciality is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Emergency Department Disposition */}
            {(!isInstitual || isNCHI) && !isDental && !isProfessionalOut && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Emergency Department Disposition
                  {(!isNCHI && isProfessionalEmer) ||
                    (isNCHI && isEmptyVisitReason && (
                      <span className="text-danger">*</span>
                    ))}{" "}
                </Form.Label>
                <Controller
                  name="emer_department_disposition"
                  control={control}
                  rules={{
                    required:
                      (!isNCHI && isProfessionalEmer) ||
                      (isNCHI && isEmptyVisitReason),
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      isMulti={false}
                      options={departmentDispositionList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setValue(
                          "emer_department_disposition",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        )
                      }
                      placeholder="Choose ..."
                      isClearable={true}
                    />
                  )}
                />
                {errors.emer_department_disposition && (
                  <Form.Text className="text-danger">
                    Emergency department disposition is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Encounter AdmitSource */}
            {isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Encounter AdmitSource{" "}
                  {isInstitual && <span className="text-danger">*</span>}
                </Form.Label>
                <Controller
                  name="encounter_admitsource"
                  control={control}
                  rules={{ required: isInstitual }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      options={adminResourceList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "encounter_admitsource",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        );
                      }}
                      formatOptionLabel={({ label }) => <div>{label}</div>}
                      getOptionValue={(option) => option.value}
                      placeholder="Choose ..."
                    />
                  )}
                />
                {errors.encounter_admitsource && (
                  <Form.Text className="text-danger">
                    Encounter admit source is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Discharge Disposition */}
            {isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Discharge Disposition
                  {(watch("discharge_date") ||
                    watch("discharge_speciality")) && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>
                <Controller
                  name="discharge_disposition"
                  control={control}
                  rules={{
                    required:
                      watch("discharge_date") || watch("discharge_speciality"),
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      isMulti={false}
                      options={dischargeDispositionList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setValue(
                          "discharge_disposition",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        )
                      }
                      placeholder="Choose ..."
                      isClearable={true}
                    />
                  )}
                />
                {errors.discharge_disposition && (
                  <Form.Text className="text-danger">
                    Discharge disposition is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Cause Of Death */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <Form.Label className="">
                Cause Of Death
                {(watch("discharge_disposition")?.enctalltyp_code === "died" ||
                  ["DED", "DOA"].includes(
                    watch("emer_department_disposition")?.enctalltyp_code
                  )) &&
                  (isInstitual || isProfessionalEmer) && (
                    <span className="text-danger">*</span>
                  )}
              </Form.Label>
              <Controller
                name="cause_of_death"
                control={control}
                rules={{
                  required:
                    (isInstitual || isProfessionalEmer) &&
                    (watch("discharge_disposition")?.enctalltyp_code ===
                      "died" ||
                      ["DED", "DOA"].includes(
                        watch("emer_department_disposition")?.enctalltyp_code
                      )),
                  validate: (value) => {
                    const dischargeCode = watch(
                      "discharge_disposition"
                    )?.enctalltyp_code;
                    const emergencyCode = watch(
                      "emer_department_disposition"
                    )?.enctalltyp_code;
                    if (
                      (isInstitual || isProfessionalEmer) &&
                      (dischargeCode === "died" ||
                        ["DED", "DOA"].includes(emergencyCode))
                    ) {
                      return (
                        !!value ||
                        "Cause of Death is required when patient died or DOA/DED"
                      );
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    isMulti={false}
                    options={preauthorizationDiagnosis?.map((item) => ({
                      value: item.code,
                      label: item.description,
                    }))}
                    value={
                      field.value
                        ? {
                            value: field.value.code,
                            label: field.value.description,
                          }
                        : null
                    }
                    onChange={(selected) =>
                      setValue(
                        "cause_of_death",
                        selected
                          ? {
                              code: selected.value,
                              description: selected.label,
                            }
                          : null
                      )
                    }
                    placeholder="Choose ..."
                    getOptionLabel={(option) =>
                      `${option.label} (${option.value})`
                    }
                    isClearable={true}
                  />
                )}
              />
              {errors.cause_of_death && (
                <Form.Text className="text-danger">
                  {errors.cause_of_death.message}
                </Form.Text>
              )}
            </Col>

            {/* Emer. Arrival Code */}
            {(!isInstitual || isNCHI) && !isDental && !isProfessionalOut && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Emer. Arrival Code{" "}
                  {(!isNCHI && isProfessionalEmer) ||
                    (isNCHI && isEmptyVisitReason && (
                      <span className="text-danger">*</span>
                    ))}
                </Form.Label>
                <Controller
                  name="emergncy_arrival_code"
                  control={control}
                  rules={{
                    required:
                      (!isNCHI && isProfessionalEmer) ||
                      (isNCHI && isEmptyVisitReason),
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      isMulti={false}
                      options={emergencyArrivalList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setValue(
                          "emergncy_arrival_code",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        )
                      }
                      placeholder="Choose ..."
                      isClearable={true}
                    />
                  )}
                />
                {errors.emergncy_arrival_code && (
                  <Form.Text className="text-danger">
                    Emergency arrival code is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Emer. Service Start Date */}
            {!isInstitual && !isDental && !isProfessionalOut && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <BootDatePicker
                  name="emergency_service_start_date"
                  label="Emer. Service Start Date"
                  size="sm"
                  control={control}
                  rules={{ required: true }}
                  selected={watch("emergency_service_start_date")}
                  onChange={(newValue) =>
                    setValue("emergency_service_start_date", newValue)
                  }
                  dateFormat="dd-mm-yyyy hh:mm"
                  showTimeSelect
                  maxDate={new Date()}
                  isRequired={true}
                />
                {errors.emergency_service_start_date && (
                  <Form.Text className="text-danger">
                    Emergency service start date is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* ReAdmission */}
            {isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">ReAdmission</Form.Label>
                <Controller
                  name="readmission"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      isMulti={false}
                      options={reAdmissionList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setValue(
                          "readmission",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        )
                      }
                      placeholder="Choose ..."
                      isClearable={true}
                    />
                  )}
                />
              </Col>
            )}

            {/* Service Event Type */}
            {!isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Service Event Type <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="service_event_type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      options={eventTypeList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "service_event_type",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        );
                      }}
                      formatOptionLabel={({ label }) => <div>{label}</div>}
                      getOptionValue={(option) => option.value}
                      placeholder="Choose ..."
                    />
                  )}
                />
                {errors.service_event_type && (
                  <Form.Text className="text-danger">
                    Service event type is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Intended Length Of Stay */}
            {isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <Form.Label className="">
                  Intended Length Of Stay{" "}
                  {isInstitual && !isNCHI && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>
                <Controller
                  name="intend_length_stay"
                  control={control}
                  rules={{ required: isInstitual && !isNCHI }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      options={lengthOfStayList?.map((item) => ({
                        value: item.enctalltyp_code,
                        label: item.enctalltyp_name,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value.enctalltyp_code,
                              label: field.value.enctalltyp_name,
                            }
                          : null
                      }
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "intend_length_stay",
                          selected
                            ? {
                                enctalltyp_code: selected.value,
                                enctalltyp_name: selected.label,
                              }
                            : null
                        );
                      }}
                      formatOptionLabel={({ label }) => <div>{label}</div>}
                      getOptionValue={(option) => option.value}
                      placeholder="Choose ..."
                    />
                  )}
                />
                {errors.intend_length_stay && (
                  <Form.Text className="text-danger">
                    Intended length of stay is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Estimated Length of Stay */}
            {isInstitual && (
              <Col xs={12} sm={6} xl={3} className="mb-3">
                <TextInput
                  name="estimated_length_stay"
                  label="Estimated Length of Stay"
                  control={control}
                  size="sm"
                  min={0}
                  max={999}
                  type="number"
                  rules={{ required: isInstitual }}
                  isRequired={isInstitual}
                />
                {errors.estimated_length_stay && (
                  <Form.Text className="text-danger">
                    Estimated length of stay is required
                  </Form.Text>
                )}
              </Col>
            )}

            {/* Last Menstrual Cycle */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <BootDatePicker
                name="last_menstrual_code"
                label="Last Menstrual Cycle"
                size="sm"
                control={control}
                rules={{ required: false }}
                selected={watch("last_menstrual_code")}
                onChange={(newValue) =>
                  setValue("last_menstrual_code", newValue)
                }
                dateFormat="dd-mm-yyyy"
                maxDate={new Date()}
                isRequired={false}
              />
            </Col>

            {/* Employment Impact Start Date */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <BootDatePicker
                name="empoly_impact_start_date"
                label="Empoly. Impact Start Date"
                size="sm"
                control={control}
                rules={{ required: false }}
                selected={watch("empoly_impact_start_date")}
                onChange={(newValue) =>
                  setValue("empoly_impact_start_date", newValue)
                }
                dateFormat="dd-mm-yyyy"
                placeholder="dd-mm-yyyy"
                isRequired={false}
              />
            </Col>

            {/* Employment Impact End Date */}
            <Col xs={12} sm={6} xl={3} className="mb-3">
              <BootDatePicker
                name="empoly_impact_end_date"
                label="Empoly. Impact End Date"
                size="sm"
                control={control}
                rules={{ required: false }}
                selected={watch("empoly_impact_end_date")}
                onChange={(newValue) =>
                  setValue("empoly_impact_end_date", newValue)
                }
                dateFormat="dd-mm-yyyy"
                placeholder="dd-mm-yyyy"
                isRequired={false}
              />
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditEncounterDetailsRequestModal;
